import { useEffect, useMemo, useRef, useState } from 'react'
import {
    useGetAnnouncementsQuery,
    useGetCalendarQuery,
    useGetKnowledgeGraphsQuery,
    useGetQandAQuery,
    useGetTooltipsQuery,
    useGetReleaseNotesQuery,
    useGetSiteFeedbackQuery,
    useGetPOCQuery,
    useGetQuickLinksQuery,
    useGetCountryQuery,
    useGetExaQuery,
    useGetFacilityCategoryQuery,
    useGetImplementingAgencyQuery,
    useGetLocationQuery,
    useGetPriorityQuery,
    useGetProgramTypeQuery,
    useGetRankQuery,
    useGetTrainingTypeQuery,
    useRefreshAnnouncementsMutation,
    useRefreshCalendarMutation,
    useRefreshKnowledgeGraphsMutation,
    useRefreshQandAMutation,
    useRefreshTooltipsMutation,
    useRefreshReleaseNotesMutation,
    useRefreshPOCMutation,
    useRefreshQuickLinksMutation,
    useRefreshCountryMutation,
    useRefreshExaMutation,
    useRefreshFacilityCategoryMutation,
    useRefreshImplementingAgencyMutation,
    useRefreshLocationMutation,
    useRefreshPriorityMutation,
    useRefreshProgramTypeMutation,
    useRefreshRankMutation,
    useRefreshTrainingTypeMutation,
} from '../../store/settings/hooks'
import { RhyBusCard } from '@rhybus/card'
import { RhyBusCalendarSettings } from '@rhybus/calendar'
import { RhyBusPageHeader } from '@rhybus/header'
import { RhyBusMetadataManagement } from '@rhybus/metadata-management'
import { RhyBusTabs } from '@rhybus/tabs'
import { RhyBusUserManagement } from '@rhybus/user-management'
import { RhyBusFeedBackSettings } from '@rhybus/site-feedback'
import {
    type ContentManagementTabKey,
    getAnnouncementItems,
    saveAnnouncementItem,
    editAnnouncementItem,
    removeAnnouncementItem,
    saveCalendarEventItem,
    editCalendarEventItem,
    removeCalendarEventItem,
    getKnowledgeGraphItems,
    saveKnowledgeGraphItem,
    editKnowledgeGraphItem,
    removeKnowledgeGraphItem,
    getQaItems,
    saveQaItem,
    editQaItem,
    removeQaItem,
    savePOCItem,
    editPOCItem,
    removePOCItem,
    getQuickLinkItems,
    saveQuickLinkItem,
    editQuickLinkItem,
    removeQuickLinkItem
} from '../../services/contentManagement'
import {
    editTooltipItem,
    metadataDefinitions,
    saveMetadataItem,
    editMetadataItem,
    removeMetadataItem,
    updateBuildVersionData,
    mapCommonMetadataForDisplay,
    resolveMetadataModifiedByNames,
    getMetadataItemsByType,
    type MetadataManagementTabKey,
    metadataManagementTabs,
} from '../../services/metadataManagement'
import {
    getReleaseNotesItems,
    editReleaseNoteItem,
    removeReleaseNoteItem,
    saveReleaseNoteItem,
} from '../../services/releaseNotes'
import { getSiteFeedbackItems } from '../../services/siteFeedback'
import {
    type AddPeopleState,
    type RoleGroup,
    type SecurityGroupMetadataItem,
    type UserInfo,
    fetchSecurityGroupMetadata,
} from '../../services/userManagement'
import './settings.css'
import { convertDate, removehtmltags } from '@rhybus/common-functions'
import { getActiveTabFromParams } from '../../services/settings'
import { rhybusWhoHttp } from '../../api/rhybus-who-http'
import { rhybusAuthCrudHttp } from '../../api/rhybus-auth-crud-http'
import { rhybusRListsHttp } from '../../api/rhybus-list-http'
import { uploadFilesToCaseBlobs, deleteDetailFile } from '../../services/detailview'
import { getUserTitle, sendUserManagementNotification } from '../../services/notifications'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { RhyBusAlert } from '@rhybus/alert'
import {
    actions, feedbackcolumns, DELETE_USER, UPDATE_BUILD_VERSION, tooltipMetadataConfig,
    countryConfig, exaConfig, facilityCategoryConfig, implementingAgencyConfig, locationConfig,
    priorityConfig, programTypeConfig, rankConfig, trainingTypeConfig, announcementsConfig,
    contentManagementTabs,
    knowledgeGraphConfig,
    qaConfig,
    userManagementConfig, releaseNotesConfig, pointOfContactConfig,
    quickLinksConfig,
    normalizeIsArchived,
    ListNames,
    VALIDATION_MSG,calendarFormConfig } from '../../config/configuration'
import SettingsPMG from '../settings-pmg/settings-pmg'
import PageOverlay from '../../utils/controls/pageoverLay/pageoverLay'
import { useTooltipResolver } from '../../services/use-tooltip-resolver'
import { enrichConfigTooltips } from '../../services/enrich-config-tooltips'
type MetadataFormConfigWithPlaceholders = {
    form?: {
        fields?: Array<{
            key: string
            placeholder?: string
        }>
    }
}

type SettingsTabConfig = {
    id: string
    title: string
}

const settingsTabs: SettingsTabConfig[] = [
    { id: 'content-management', title: 'Content Management' },
    { id: 'metadata-management', title: 'Metadata Management' },
    { id: 'user-management', title: 'User Management' },
    { id: 'release-notes', title: 'Release Notes' },
    { id: 'site-feedback', title: 'Site Feedback' }
]

const isArchivedValue = (value: any) =>
    value === true || value === 1 || value === '1' || value === 'Yes' || value === 'true'

export default function Settings() {
    const { data: announcementsData, error: announcementsError } = useGetAnnouncementsQuery()
    const { data: calendarData } = useGetCalendarQuery()
    const { data: knowledgeGraphsData, error: knowledgeGraphsError } = useGetKnowledgeGraphsQuery()
    const { data: qandaData, error: qandaError } = useGetQandAQuery()
    const { data: tooltipsData } = useGetTooltipsQuery()
    const { data: releaseNotesData } = useGetReleaseNotesQuery()
    const { data: siteFeedbackData } = useGetSiteFeedbackQuery()
    const { data: pocData, error: pocError } = useGetPOCQuery()
    const { data: quickLinksData, error: quickLinksError } = useGetQuickLinksQuery()
    const { data: countryData, error: countryError } = useGetCountryQuery()
    const { data: exaData, error: exaError } = useGetExaQuery()
    const { data: facilityCategoryData, error: facilityCategoryError } = useGetFacilityCategoryQuery()
    const { data: implementingAgencyData, error: implementingAgencyError } = useGetImplementingAgencyQuery()
    const { data: locationData, error: locationError } = useGetLocationQuery()
    const { data: priorityData, error: priorityError } = useGetPriorityQuery()
    const { data: programTypeData, error: programTypeError } = useGetProgramTypeQuery()
    const { data: rankData, error: rankError } = useGetRankQuery()
    const { data: trainingTypeData, error: trainingTypeError } = useGetTrainingTypeQuery()
    useEffect(() => {
        if (announcementsError) console.error('[Settings] announcements query error:', announcementsError)
        if (knowledgeGraphsError) console.error('[Settings] Knowledge Bases query error:', knowledgeGraphsError)
        if (qandaError) console.error('[Settings] Q&A query error:', qandaError)
        if (pocError) console.error('[Settings] POC query error:', pocError)
        if (quickLinksError) console.error('[Settings] quick links query error:', quickLinksError)
        if (countryError) console.error('[Settings] country query error:', countryError)
        if (exaError) console.error('[Settings] exa query error:', exaError)
        if (facilityCategoryError) console.error('[Settings] facilityCategory query error:', facilityCategoryError)
        if (implementingAgencyError) console.error('[Settings] implementingAgency query error:', implementingAgencyError)
        if (locationError) console.error('[Settings] location query error:', locationError)
        if (priorityError) console.error('[Settings] priority query error:', priorityError)
        if (programTypeError) console.error('[Settings] programType query error:', programTypeError)
        if (rankError) console.error('[Settings] rank query error:', rankError)
        if (trainingTypeError) console.error('[Settings] trainingType query error:', trainingTypeError)
    }, [announcementsError, knowledgeGraphsError, qandaError, pocError, quickLinksError,
        countryError, exaError, facilityCategoryError, implementingAgencyError, locationError,
        priorityError, programTypeError, rankError, trainingTypeError])

    const [refreshAnnouncements] = useRefreshAnnouncementsMutation()
    const [refreshCalendar] = useRefreshCalendarMutation()
    const [refreshKnowledgeGraphs] = useRefreshKnowledgeGraphsMutation()
    const [refreshQandA] = useRefreshQandAMutation()
    const [refreshTooltips] = useRefreshTooltipsMutation()
    const [refreshReleaseNotes] = useRefreshReleaseNotesMutation()
    const [refreshPOC] = useRefreshPOCMutation()
    const [refreshQuickLinks] = useRefreshQuickLinksMutation()
    const [refreshCountry] = useRefreshCountryMutation()
    const [refreshExa] = useRefreshExaMutation()
    const [refreshFacilityCategory] = useRefreshFacilityCategoryMutation()
    const [refreshImplementingAgency] = useRefreshImplementingAgencyMutation()
    const [refreshLocation] = useRefreshLocationMutation()
    const [refreshPriority] = useRefreshPriorityMutation()
    const [refreshProgramType] = useRefreshProgramTypeMutation()
    const [refreshRank] = useRefreshRankMutation()
    const [refreshTrainingType] = useRefreshTrainingTypeMutation()

    const [activeTab, setActiveTab] = useState(0)
    const [searchParams] = useSearchParams()
    const [activeContentManagementTab, setActiveContentManagementTab] = useState<ContentManagementTabKey>('announcements')
    const [activeMetadataManagementTab, setActiveMetadataManagementTab] = useState<MetadataManagementTabKey>('country')
    const [metadataTabsKey, setMetadataTabsKey] = useState(0)
    const [calendarKey, setCalendarKey] = useState(0)
    const pendingCalendarRefresh = useRef(false)
    const rawFileCache = useRef<Map<string, File>>(new Map())

    const uploadCachedFiles = async (eKey: string, attachmentFiles: any, pageName: string): Promise<void> => {
        const fileArray: any[] = Array.isArray(attachmentFiles) ? attachmentFiles
            : attachmentFiles instanceof FileList ? Array.from(attachmentFiles)
            : attachmentFiles ? [attachmentFiles] : []
        const newFiles = fileArray
            .map((f: any) => rawFileCache.current.get(`${f.name}_${f.size}`))
            .filter((f): f is File => f instanceof File)
        if (newFiles.length > 0) {
            await uploadFilesToCaseBlobs(eKey, newFiles, undefined, pageName, pageName)
            newFiles.forEach((f) => rawFileCache.current.delete(`${f.name}_${f.size}`))
        }
    }
    const _navigate = useNavigate()
    const [accessGroups, setAccessGroups] = useState<string[]>([])

    useEffect(() => {
        setActiveTab(getActiveTabFromParams(searchParams))
    }, [searchParams])

    useEffect(() => {
        if (pendingCalendarRefresh.current) {
            pendingCalendarRefresh.current = false
            setCalendarKey(k => k + 1)
        }
    }, [calendarData])

    useEffect(() => {
        const capture = (e: Event) => {
            const input = e.target as HTMLInputElement
            if (input.type !== 'file' || !input.files?.length) return
            Array.from(input.files).forEach((f) => rawFileCache.current.set(`${f.name}_${f.size}`, f))
        }
        const captureDrop = (e: DragEvent) => {
            const files = e.dataTransfer?.files
            if (!files?.length) return
            Array.from(files).forEach((f) => rawFileCache.current.set(`${f.name}_${f.size}`, f))
        }
        document.addEventListener('change', capture, true)
        document.addEventListener('drop', captureDrop, true)
        return () => {
            document.removeEventListener('change', capture, true)
            document.removeEventListener('drop', captureDrop, true)
        }
    }, [])
    const [loading, setLoading] = useState(false)
    const [dataLoading, setDataLoading] = useState(true)
    const [showAlert, setShowAlert] = useState(false)
    const [deleteItem, setDeleteItem] = useState<UserInfo | null>(null)
    const [deleteSelectedRole, setDeleteSelectedRole] = useState('')
    const [_deleteActiveIndex, setDeleteActiveIndex] = useState(0)
    const [validationAlert, setValidationAlert] = useState(false)
    const [roleUsers, setRoleUsers] = useState<RoleGroup[]>([])
    const [userAccessGroups, setUserAccessGroups] = useState<string[]>([])
    const [securityGroupMetadata, setSecurityGroupMetadata] = useState<SecurityGroupMetadataItem[]>([])
    const [securityGroupLoading, setSecurityGroupLoading] = useState(false)
    const [securityGroupError, setSecurityGroupError] = useState<string | null>(null)
    const [userRoleFilter, setUserRoleFilter] = useState('')
    const [peoplePickerSuggestions, setPeoplePickerSuggestions] = useState<any[]>([])
    const [settingsAnnouncementsData, setSettingsAnnouncementsData] = useState<any[] | null>(null)
    const [settingsKnowledgeGraphData, setSettingsKnowledgeGraphData] = useState<any[] | null>(null)
    const [settingsQaData, setSettingsQaData] = useState<any[] | null>(null)
    const [settingsReleaseNotesData, setSettingsReleaseNotesData] = useState<any[] | null>(null)
    const [settingsSiteFeedbackData, setSettingsSiteFeedbackData] = useState<any[] | null>(null)
    const [settingsQuickLinksData, setSettingsQuickLinksData] = useState<any[] | null>(null)
    const [settingsMetadataCache, setSettingsMetadataCache] = useState<Record<string, any[]>>({})

    const loadSettingsAnnouncements = async () => {
        const items = await getAnnouncementItems()
        setSettingsAnnouncementsData(items)
    }

    const loadSettingsKnowledgeGraph = async () => {
        const items = await getKnowledgeGraphItems()
        setSettingsKnowledgeGraphData(items)
    }

    const loadSettingsQa = async () => {
        const items = await getQaItems()
        setSettingsQaData(items)
    }

    const loadSettingsReleaseNotes = async () => {
        const items = await getReleaseNotesItems()
        setSettingsReleaseNotesData(items)
    }

    const loadSettingsSiteFeedback = async () => {
        const items = await getSiteFeedbackItems()
        setSettingsSiteFeedbackData(items)
    }

    const loadSettingsQuickLinks = async () => {
        const items = await getQuickLinkItems()
        setSettingsQuickLinksData(items)
    }

    const loadSettingsMetadataSection = async (sectionKey: string) => {
        const definition = metadataDefinitions.find(d => d.key === sectionKey)
        if (!definition) return
        const items = await getMetadataItemsByType(definition.metaDataType)
        const resolved = await resolveMetadataModifiedByNames(items)
        const mapped = mapCommonMetadataForDisplay(resolved)
        setSettingsMetadataCache(prev => ({ ...prev, [sectionKey]: mapped }))
    }

    useEffect(() => {
        const loadAll = async () => {
            setDataLoading(true)
            try {
                await Promise.all([
                    loadSettingsAnnouncements(),
                    loadSettingsKnowledgeGraph(),
                    loadSettingsQa(),
                    loadSettingsReleaseNotes(),
                    loadSettingsSiteFeedback(),
                    loadSettingsQuickLinks(),
                ])
            } finally {
                setDataLoading(false)
            }
        }
        loadAll()
    }, [])

    // ─── derived display arrays — computed directly from RTK Query data ────────
    const announcementsArray = useMemo<any[]>(() =>
        (settingsAnnouncementsData ?? announcementsData ?? []).filter((a: any) => !!a?.Title).map((a: any) => {
            const itemId = a.ID ?? a.Id ?? a._Id ?? a.eKey
            const modifiedByName = typeof a.ItemModifiedBy === 'string'
                ? a.ItemModifiedBy
                : a.ItemModifiedBy?.Title ?? a.modifiedBy ?? ''
            return {
                ...a,
                ID: itemId, Id: itemId, Title: a.Title,
                Description: a.Description,
                Modified: (modifiedByName ? modifiedByName + ' | ' : '') + convertDate(a.ItemModified ?? a.modifiedOn, 'date'),
                Tags: a.Tags, ExpiryDate: a.ExpiryDate,
                AttachmentFiles: a.AttachmentFiles ?? [],
                IsArchived: isArchivedValue(a.IsArchived) ? 'Yes' : 'No',
            }
        }), [announcementsData, settingsAnnouncementsData])


    const knowledgeGraphArray = useMemo<any[]>(() =>
        (settingsKnowledgeGraphData ?? knowledgeGraphsData ?? []).filter((a: any) => !!a?.Title).map((a: any) => {
            const itemId = a.ID ?? a.Id ?? a._Id ?? a.eKey
            const modifiedByName = typeof a.ItemModifiedBy === 'string'
                ? a.ItemModifiedBy
                : a.ItemModifiedBy?.Title ?? a.modifiedBy ?? ''
            return {
                ...a,
                ID: itemId, Id: itemId, Title: a.Title,
                Description: a.Description,
                Modified: (modifiedByName ? modifiedByName + ' | ' : '') + convertDate(a.ItemModified ?? a.modifiedOn, 'date'),
                Tags: a.Tags ?? '', AttachmentFiles: a.AttachmentFiles ?? [],
                IsArchived: normalizeIsArchived(a.IsArchived) ? 'Yes' : 'No',
            }
        }), [knowledgeGraphsData, settingsKnowledgeGraphData])

    const qaArray = useMemo<any[]>(() =>
        (settingsQaData ?? qandaData ?? []).filter((q: any) => !!(q?.ID ?? q?.Id ?? q?._Id ?? q?.eKey)).map((q: any) => {
            const itemId = q.ID ?? q.Id ?? q._Id ?? q.eKey
            const modifiedByName = typeof q.ItemModifiedBy === 'string'
                ? q.ItemModifiedBy
                : q.ItemModifiedBy?.Title ?? q.modifiedBy ?? ''
            return {
                ...q,
                ID: itemId, Id: itemId, Title: q.Question ?? q.Title, Question: q.Question ?? q.Title,
                Modified: (modifiedByName ? modifiedByName + ' | ' : '') + convertDate(q.ItemModified ?? q.modifiedOn, 'date'),
                Description:q.Description,
                IsArchived: isArchivedValue(q.IsArchived) ? 'Yes' : 'No',
                Answer: q.Answer,
                AttachmentFiles: q.AttachmentFiles ?? [], Tags: q.Tags ?? '',
            }
        }), [qandaData, settingsQaData])

    const resolveTooltip = useTooltipResolver()

    const tooltipArray = useMemo<any[]>(() =>
        (tooltipsData ?? []).filter((t: any) => !!t?.TooltipID).map((t: any) => {
            const modifiedBy = t.ItemModifiedBy?.Title ?? (typeof t.ItemModifiedBy === 'string' ? t.ItemModifiedBy : '')
            const rawDate = t.ItemModified ?? t.modifiedOn ?? t.ItemCreated ?? t.createdOn
            const modifiedDate = rawDate ? (convertDate(rawDate, 'date') ?? '') : ''
            return {
                ID: t.ID ?? t._Id, Id: t._Id ?? t.ID, Title: t.TooltipName,
                Modified: [modifiedBy, modifiedDate].filter(Boolean).join(' | '),
                ToolTipID: t.TooltipID, LabelName: t.TooltipName,
                TooltipDescription: t.TooltipDescription,
                eKey: t.eKey, eTag: t.eTag,
            }
        }), [tooltipsData])

    const enrichedTooltipMetadataConfig = useMemo(
        () => enrichConfigTooltips(tooltipMetadataConfig, resolveTooltip),
        [resolveTooltip]
    )

    const countryArray = useMemo(() => mapCommonMetadataForDisplay(countryData ?? []), [countryData])
    const exaArray = useMemo(() => mapCommonMetadataForDisplay(exaData ?? []), [exaData])
    const facilityCategoryArray = useMemo(() => mapCommonMetadataForDisplay(facilityCategoryData ?? []), [facilityCategoryData])
    const implementingAgencyArray = useMemo(() => mapCommonMetadataForDisplay(implementingAgencyData ?? []), [implementingAgencyData])
    const locationArray = useMemo(() => mapCommonMetadataForDisplay(locationData ?? []), [locationData])
    const priorityArray = useMemo(() => mapCommonMetadataForDisplay(priorityData ?? []), [priorityData])
    const programTypeArray = useMemo(() => mapCommonMetadataForDisplay(programTypeData ?? []), [programTypeData])
    const rankArray = useMemo(() => mapCommonMetadataForDisplay(rankData ?? []), [rankData])
    const trainingTypeArray = useMemo(() => mapCommonMetadataForDisplay(trainingTypeData ?? []), [trainingTypeData])

    const releaseNotesArray = useMemo<any[]>(() =>
        (settingsReleaseNotesData ?? releaseNotesData ?? []).filter((r: any) => !!r?.VersionName || !!r?.Title).map((r: any) => {
            const itemId = r.ID ?? r.Id ?? r._Id ?? r.eKey
            const modifiedByName = typeof r.ItemModifiedBy === 'string'
                ? r.ItemModifiedBy
                : r.ItemModifiedBy?.Title ?? r.modifiedBy ?? ''
            return {
                ...r,
                ID: itemId, Id: itemId, Title: r.VersionName ?? r.Title, VersionName: r.VersionName ?? r.Title,
                KnownIssues: removehtmltags(r.KnownIssues),
                NewFeatures: removehtmltags(r.NewFeatures),
                ResolvedIssues: removehtmltags(r.ResolvedIssues),
                Modified: (modifiedByName ? modifiedByName + ' | ' : '') + convertDate(r.ItemModified ?? r.modifiedOn, 'date'),
                ReleaseDate: convertDate(r.ReleaseDate, ''),
                AttachmentFiles: r.AttachmentFiles ?? [],
                IsArchived: isArchivedValue(r.IsArchived) ? 'Yes' : 'No',
            }
        }), [releaseNotesData, settingsReleaseNotesData])

    const pocArray = useMemo<any[]>(() =>
        [...(pocData ?? [])]
            .sort((a: any, b: any) => new Date(b.ItemModified ?? b.Modified ?? 0).getTime() - new Date(a.ItemModified ?? a.Modified ?? 0).getTime())
            .filter((item: any) => !!item?.PointOfContact)
            .map((item: any) => {
                const modifiedByName = item?.ItemModifiedBy?.Title ?? item?.Editor?.Title ?? ''
                const modifiedDate = item?.ItemModified ?? item?.Modified
                return {
                    ...item,
                    ID: item.ID, Id: item.ID,
                    ContactName: item.PointOfContact
                        ? { DisplayText: item.PointOfContact, email: item.Title ?? '', persona: item.PointOfContact }
                        : '',
                    ContactTitle: item.DutyTitle,
                    ContactEmail: item.Title || item.ContactEmail, ContactPhone: item.PhoneNumber || item.ContactPhone,
                    Modified: (modifiedByName ? modifiedByName + ' | ' : '') + convertDate(modifiedDate, 'date'),
                    IsArchived: item.IsArchived ? 'Yes' : 'No',
                }
            }), [pocData])

    const quickLinksArray = useMemo<any[]>(() =>
        [...(settingsQuickLinksData ?? quickLinksData ?? [])]
            .sort((a: any, b: any) => new Date(b.ItemModified ?? b.Modified ?? 0).getTime() - new Date(a.ItemModified ?? a.Modified ?? 0).getTime())
            .filter((item: any) => !!item?.Title)
            .map((item: any) => {
                const itemId = item.ID ?? item.Id ?? item._Id ?? item.eKey
                const modifiedByName = typeof item?.ItemModifiedBy === 'string'
                    ? item.ItemModifiedBy
                    : item?.ItemModifiedBy?.Title ?? item?.Editor?.Title ?? item?.modifiedBy ?? ''
                const modifiedDate = item?.ItemModified ?? item?.Modified ?? item?.modifiedOn
                return {
                    ...item,
                    ID: itemId, Id: itemId, Title: item.Title, URL: item.URL,
                    Info: item.Info,
                    Modified: (modifiedByName ? modifiedByName + ' | ' : '') + convertDate(modifiedDate, 'date'),
                    IsArchived: normalizeIsArchived(item.IsArchived) ? 'Yes' : 'No',
                }
            }), [quickLinksData, settingsQuickLinksData])

    const getManagementDataKey = (sectionKey: string, items: any[]) =>
        `${sectionKey}-${items.map(item => `${item.ID ?? item.Id}:${item.Modified ?? ''}`).join('|')}`

    const getActiveMetadataFormConfig = (): MetadataFormConfigWithPlaceholders | null => {
        const activeSettingsTab = settingsTabs[activeTab]?.id
        if (activeSettingsTab === 'content-management') {
            if (activeContentManagementTab === 'announcements') return announcementsConfig
            if (activeContentManagementTab === 'knowledge-base') return knowledgeGraphConfig
            if (activeContentManagementTab === 'qa') return qaConfig
            return null
        }
        if (activeSettingsTab === 'metadata-management') {
            return tooltipMetadataConfig
        }
        if (activeSettingsTab === 'release-notes') {
            return releaseNotesConfig
        }
        return null
    }

    const syncMetadataFormPlaceholders = (config: MetadataFormConfigWithPlaceholders | null) => {
        config?.form?.fields?.forEach((field) => {
            if (!field.placeholder) {
                return
            }
            const fieldElement = document.getElementById(field.key) as HTMLInputElement | null
            if (fieldElement && !fieldElement.getAttribute('placeholder')) {
                fieldElement.setAttribute('placeholder', field.placeholder)
            }
        })
    }
    useEffect(() => {
        const applyPlaceholders = () => {
            syncMetadataFormPlaceholders(getActiveMetadataFormConfig())
        }
        applyPlaceholders()
        const ObserverConstructor = window.MutationObserver
        if (!ObserverConstructor) {
            return undefined
        }
        const observer = new ObserverConstructor(() => {
            applyPlaceholders()
        })
        observer.observe(document.body, {
            childList: true,
            subtree: true
        })
        return () => observer.disconnect()
    }, [activeTab, activeContentManagementTab, activeMetadataManagementTab])

    useEffect(() => {
        const METADATA_TAB_INDEX = settingsTabs.findIndex(t => t.id === 'metadata-management')
        if (activeTab !== METADATA_TAB_INDEX) return
        loadSettingsMetadataSection(activeMetadataManagementTab)
    }, [activeTab, activeMetadataManagementTab])

    // ─── people-picker / user handlers ───────────────────────────────────────
    const getAllGroupNamesFromRoleAccess = async (groupNames: string[]): Promise<string[]> => {
        const roles = [...(await rhybusAuthCrudHttp().GetRoles())]
        const roleSet = new Set(
            roles.map(r => r?.name?.trim()?.toLowerCase())
        )
        return groupNames.filter(name =>
            roleSet.has(name?.toLowerCase())
        )
    }

    const getUserAccessGroupsList = async (groupNames: string[]): Promise<string[]> => {
        const roleAccessedGroups = await getAllGroupNamesFromRoleAccess(groupNames)
        return [...new Set(roleAccessedGroups)]
    }

    const enrichUserInfo = async (user: any): Promise<UserInfo> => {
        const loginKey: string =
            user?.LoginName ?? user?.loginName ?? user?.upn ?? user?.userId ?? user?.rid ?? user?.email ?? ''
        const existingTitle: string =
            user?.Title ?? user?.persona ?? user?.displayName ?? user?.DisplayText ?? user?.profileName ?? user?.name ?? user?.cn ?? ''
        const existingEmail: string = user?.Email ?? user?.email ?? user?.upn ?? ''

        if (existingTitle && existingEmail) {
            return { ...user, Title: existingTitle, Email: existingEmail, LoginName: loginKey } as UserInfo
        }

        if (loginKey) {
            try {
                const results = [...(await rhybusWhoHttp().Picker(loginKey))]
                if (results.length > 0) {
                    const picked = results[0] as any
                    return {
                        ...user,
                        Title: picked?.persona ?? picked?.displayName ?? picked?.DisplayText ?? picked?.profileName ?? picked?.name ?? existingTitle,
                        Email: picked?.email ?? picked?.upn ?? existingEmail,
                        LoginName: loginKey,
                    } as UserInfo
                }
            } catch (err) {
                console.error('Error enriching user data:', err)
            }
        }

        return { ...user, Title: existingTitle, Email: existingEmail, LoginName: loginKey } as UserInfo
    }

    const getUsersfromAllRoles = async (groupsToFetch?: string[]) => {
        const groups = groupsToFetch ?? accessGroups
        const allRoles: RoleGroup[] = []
        await Promise.all(
            groups?.map(async (i: string, index: number) => {
                const rawUsers = [...(await rhybusWhoHttp().Role(i))] as unknown as any[]
                const rolesgroup = await Promise.all(rawUsers.map(enrichUserInfo))
                allRoles.push({ role: i, roleUsers: rolesgroup, is_active: index === 0 ? 'true' : 'false' })
            }) ?? []
        )
        setRoleUsers(allRoles.sort((a, b) => a.role?.localeCompare(b.role)))
        //setLoading(false)
    }

    const fetchGroups = async () => {
        setSecurityGroupLoading(true)
        setSecurityGroupError(null)
        try {
            const metadata = await fetchSecurityGroupMetadata()
            setSecurityGroupMetadata(metadata)
            const groupNames = metadata.map((item) => item.SecurityGroupTitle).filter(Boolean)
            setAccessGroups(groupNames)
            const accessibleGroups = await getUserAccessGroupsList(groupNames)
            setUserAccessGroups(accessibleGroups)
            await getUsersfromAllRoles(groupNames)
        } catch (error) {
            console.error('Error fetching security group metadata:', error)
            setSecurityGroupError('Failed to load security groups.')
        } finally {
            setSecurityGroupLoading(false)
        }
    }
    useEffect(() => {
        fetchGroups()
    }, [])
    const getPickerDisplayName = (result: any): string => (
        result?.persona
        ?? result?.displayName
        ?? result?.DisplayText
        ?? result?.cn
        ?? result?.profileName
        ?? result?.name
        ?? result?.title
        ?? result?.email
        ?? ''
    )

    const getPickerUserKey = (result: any): string => (
        result?.rid
        ?? result?.userId
        ?? result?.Key
        ?? result?.spKey
        ?? result?.edipi
        ?? result?.upn
        ?? result?.email
        ?? ''
    )

    const handlePeoplePickerChange = async (value: string) => {
        if (!value) {
            setPeoplePickerSuggestions([])
            return
        }
        try {
            const results = [...(await rhybusWhoHttp().Picker(value))]
            const formatted = results
                .map((result: any) => {
                    const displayName = getPickerDisplayName(result)
                    const userKey = getPickerUserKey(result)
                    return {
                        DisplayText: displayName,
                        email: result?.email ?? result?.upn ?? '',
                        persona: displayName,
                        spKey: userKey,
                    }
                })
                .filter((person) => person.DisplayText || person.email || person.spKey)
            setPeoplePickerSuggestions(formatted)
        } catch (err) {
            console.error('Error fetching people picker suggestions:', err)
            setPeoplePickerSuggestions([])
        }
    }

    const handleRefresh = async() => {
        setRoleUsers([])
        await fetchGroups()
    }

    const closeAddRemovePopup = () => {
        document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }))
    }

    const handleAddUser = async (
        addpeople: AddPeopleState,
        roles: string[]
    ) => {
        closeAddRemovePopup()
        setLoading(true)
        try {
            await Promise.all(roles.map((role) => rhybusAuthCrudHttp().RoleAddUser(role, addpeople.userKey)))
            const userName = await getUserTitle(addpeople.userKey, addpeople.userEmail || addpeople.userKey)
            // await sendUserManagementNotification('add', userName)
            await handleRefresh()
        } catch (error) {
            console.error('Error adding users to roles:', error)
        } finally {
            setLoading(false)
        }
    }
    const handleRemoveUser = async (
        addpeople: AddPeopleState,
        roles: string[]
    ) => {
        closeAddRemovePopup()
        try {
            await Promise.all(roles.map((role) => rhybusAuthCrudHttp().RoleRemoveUser(role, addpeople.userKey)))
            const removedKey = addpeople.userKey?.toLowerCase()
            const removedEmail = addpeople.userEmail?.toLowerCase()
            const normalizedRoles = roles.map(r => r.toLowerCase())
            setRoleUsers(prev => prev.map(group =>
                normalizedRoles.includes(group.role?.toLowerCase())
                    ? { ...group, roleUsers: group.roleUsers.filter(u => {
                        const loginName = u.LoginName?.toLowerCase()
                        const uEmail = u.Email?.toLowerCase()
                        return !(loginName === removedKey || uEmail === removedKey || loginName === removedEmail || uEmail === removedEmail)
                      })}
                    : group
            ))
            sendUserManagementNotification('delete', addpeople.userEmail || addpeople.userKey)
                .catch(err => console.error('Error sending remove notification:', err))
        } catch (error) {
            console.error('Error removing users from roles:', error)
        }
    }
    const handleDeleteUser = (
        selectedUser: UserInfo,
        selectedRole: string,
        activeIndex: number
    ) => {
        if (accessGroups?.includes(selectedRole)) {
            setDeleteItem(selectedUser)
            setDeleteSelectedRole(selectedRole)
            setDeleteActiveIndex(activeIndex)
            setShowAlert(true)
        } else {
            setValidationAlert(true)
        }
    }


    const handleAlertClick = async (button: string) => {
        if (button === 'Yes') {
            setShowAlert(false)
            if (deleteItem) {
                //  setLoading(true)
                const userKey = getPickerUserKey(deleteItem) || deleteItem.LoginName
                await rhybusAuthCrudHttp().RoleRemoveUser(deleteSelectedRole, userKey)
                // await sendUserManagementNotification('delete', deleteItem.Title || deleteItem.Email || deleteItem.LoginName)
                const deletedKey = userKey?.toLowerCase()
                const deletedEmail = deleteItem.Email?.toLowerCase()
                setRoleUsers(prev => prev.map(group =>
                    group.role?.toLowerCase() === deleteSelectedRole?.toLowerCase()
                        ? { ...group, roleUsers: group.roleUsers.filter(u => {
                            const loginName = u.LoginName?.toLowerCase()
                            const uEmail = u.Email?.toLowerCase()
                            return !(loginName === deletedKey || uEmail === deletedKey || loginName === deletedEmail || uEmail === deletedEmail)
                          })}
                        : group
                ))
                // Check if deleting current user - may need RhyBus API implementation
                // if (deleteItem.Email === currentUserEmail) {
                //     navigate('/settings', { state: { activeTab: 2, selectedRole: deleteSelectedRole, activeIndex: deleteActiveIndex } })
                // }
                //   setLoading(false)
            }
            setDeleteItem(null)
        } else {
            setShowAlert(false)
            setDeleteItem(null)
        }
    }

    const getUserRolesFiltered = async (userKey: string): Promise<string[]> => {
        try {
            const user = await rhybusAuthCrudHttp().GetUser(userKey)
            const userRoles: string[] = user?.roles ?? []
            return userRoles.filter((role) =>
                accessGroups.some((g) => g.toLowerCase() === role.toLowerCase())
            )
        } catch {
            return []
        }
    }

    // ─── content management render ───────────────────────────────────────────
    const renderContentManagementSection = (sectionKey: ContentManagementTabKey) => {
        if (sectionKey === 'announcements') {
            return (
                <RhyBusMetadataManagement
                    key={getManagementDataKey('announcements', announcementsArray)}
                    config={announcementsConfig}
                    data={announcementsArray}
                    isAdd={true}
                    isEdit={true}
                    isDelete={true}
                    onAdd={async (item) => {
                        setLoading(true)
                        try {
                            const eKey = await saveAnnouncementItem(item)
                            if (eKey) await uploadCachedFiles(eKey, (item as any).AttachmentFiles ?? [], 'announcements')
                            await refreshAnnouncements()
                            await loadSettingsAnnouncements()
                        } finally { setLoading(false) }
                    }}
                    onUpdate={async (item) => {
                        setLoading(true)
                        try {
                            const original = announcementsArray.find((a: any) => a.ID === (item as any).ID || a.eKey === (item as any).ID)
                            const eKey = await editAnnouncementItem({ ...original, ...item })
                            if (eKey) await uploadCachedFiles(eKey, (item as any).AttachmentFiles ?? [], 'announcements')
                            await refreshAnnouncements()
                            await loadSettingsAnnouncements()
                        } finally { setLoading(false) }
                    }}
                    onDelete={async (item) => {
                        setLoading(true)
                        try {
                            await removeAnnouncementItem(item)
                            await refreshAnnouncements()
                            await loadSettingsAnnouncements()
                        } finally { setLoading(false) }
                    }}
                />
            )
        }

        if (sectionKey === 'eventcalendar') {
            return (
                <div className="settings-calendar-panel">
                    <RhyBusCalendarSettings
                        key={calendarKey}
                        formConfig={calendarFormConfig}
                        CalendarData={() => Promise.resolve(calendarData ?? [])}
                        onAddEvent={async (event, files) => {
                            setLoading(true)
                            try {
                                const eKey = await saveCalendarEventItem(event, files)
                                if (eKey) {
                                    const newFiles: File[] = (Array.isArray(files) ? files : [])
                                        .map((f: any) => f instanceof File ? f : f?.content?.file ?? f?.content ?? f?.file)
                                        .filter((f): f is File => f instanceof File)
                                    if (newFiles.length > 0) {
                                        await uploadFilesToCaseBlobs(eKey, newFiles, undefined, 'eventcalendar', 'eventcalendar')
                                    }
                                }
                                pendingCalendarRefresh.current = true
                                await refreshCalendar()
                                return { success: true }
                            } catch (error) {
                                return { success: false, error }
                            } finally { setLoading(false) }
                        }}
                        onUpdateEvent={async (eventId: number, eventData: any, files: any, filesToDelete: string[]) => {
                            setLoading(true)
                            try {
                                const target = (calendarData ?? []).find(e => e.ID === eventId || e.Id === eventId)
                                const eKey = await editCalendarEventItem({ ...target, ID: eventId, ...eventData }, files)
                                if (eKey) {
                                    if (filesToDelete?.length > 0) {
                                        await Promise.all(
                                            filesToDelete.map((fileName: string) =>
                                                deleteDetailFile(eKey, { Name: fileName }, 'eventcalendar')
                                            )
                                        )
                                    }
                                    const newFiles: File[] = (Array.isArray(files) ? files : [])
                                        .map((f: any) => f instanceof File ? f : f?.content?.file ?? f?.content ?? f?.file)
                                        .filter((f): f is File => f instanceof File)
                                    if (newFiles.length > 0) {
                                        await uploadFilesToCaseBlobs(eKey, newFiles, undefined, 'eventcalendar', 'eventcalendar')
                                    }
                                }
                                pendingCalendarRefresh.current = true
                                await refreshCalendar()
                                return { success: true }
                            } catch (error) {
                                return { success: false, error }
                            } finally { setLoading(false) }
                        }}
                        onDeleteEvent={async (eventId: number) => {
                            setLoading(true)
                            try {
                                const target = (calendarData ?? []).find(e => e.ID === eventId)
                                if (!target) return { success: false, error: 'Event not found' }
                                await removeCalendarEventItem(target)
                                pendingCalendarRefresh.current = true
                                await refreshCalendar()
                                return { success: true }
                            } catch (error) {
                                return { success: false, error }
                            } finally { setLoading(false) }
                        }}
                    />
                </div>
            )
        }

        if (sectionKey === 'knowledge-base') {
            return (
                <RhyBusMetadataManagement
                    key={getManagementDataKey('knowledge-base', knowledgeGraphArray)}
                    config={knowledgeGraphConfig}
                    data={knowledgeGraphArray}
                    onAdd={async (item) => {
                        setLoading(true)
                        try {
                            const eKey = await saveKnowledgeGraphItem(item)
                            if (eKey) await uploadCachedFiles(eKey, (item as any).AttachmentFiles ?? [], 'knowledge-base')
                            await refreshKnowledgeGraphs()
                            await loadSettingsKnowledgeGraph()
                        } finally { setLoading(false) }
                    }}
                    onUpdate={async (item) => {
                        setLoading(true)
                        try {
                            const original = knowledgeGraphArray.find((a: any) => a.ID === (item as any).ID || a.eKey === (item as any).ID)
                            const eKey = await editKnowledgeGraphItem({ ...original, ...item })
                            if (eKey) await uploadCachedFiles(eKey, (item as any).AttachmentFiles ?? [], 'knowledge-base')
                            await refreshKnowledgeGraphs()
                            await loadSettingsKnowledgeGraph()
                        } finally { setLoading(false) }
                    }}
                    onDelete={async (item) => {
                        setLoading(true)
                        try {
                            await removeKnowledgeGraphItem(item)
                            await refreshKnowledgeGraphs()
                            await loadSettingsKnowledgeGraph()
                        } finally { setLoading(false) }
                    }}
                    isDelete={true}
                    isEdit={true}
                    isAdd={true}
                />
            )
        }

        if (sectionKey === 'point-of-contact') {
            return (
                <RhyBusMetadataManagement
                    key={getManagementDataKey('point-of-contact', pocArray)}
                    config={pointOfContactConfig}
                    data={pocArray}
                    onAdd={async (item) => {
                        setLoading(true)
                        try {
                            await savePOCItem(item)
                            await refreshPOC()
                        } finally { setLoading(false) }
                    }}
                    onUpdate={async (item) => {
                        const original = pocArray.find((p: any) => p.ID === (item as any).ID || p.eKey === (item as any).ID)
                        await editPOCItem({ ...original, ...item })
                        await refreshPOC()
                    }}
                    onDelete={async (item) => {
                        setLoading(true)
                        try {
                            await removePOCItem(item)
                            await refreshPOC()
                        } finally { setLoading(false) }
                    }}
                    isDelete={true}
                    isEdit={true}
                    isAdd={true}
                />
            )
        }
         if (sectionKey === 'Policy Memos & Guidelines') {
            return (
               <SettingsPMG/>
            )
        }
        if (sectionKey === 'quick-links') {
            return (
                <RhyBusMetadataManagement
                    key={getManagementDataKey('quick-links', quickLinksArray)}
                    config={quickLinksConfig}
                    data={quickLinksArray}
                    onAdd={async (item) => {
                        setLoading(true)
                        try {
                            await saveQuickLinkItem(item)
                            await refreshQuickLinks()
                            await loadSettingsQuickLinks()
                        } finally { setLoading(false) }
                    }}
                    onUpdate={async (item) => {
                        setLoading(true)
                        try {
                            const original = quickLinksArray.find((a: any) => a.ID === (item as any).ID || a.eKey === (item as any).ID)
                            await editQuickLinkItem({ ...original, ...item })
                            await refreshQuickLinks()
                            await loadSettingsQuickLinks()
                        } finally { setLoading(false) }
                    }}
                    onDelete={async (item) => {
                        setLoading(true)
                        try {
                            await removeQuickLinkItem(item)
                            await refreshQuickLinks()
                            await loadSettingsQuickLinks()
                        } finally { setLoading(false) }
                    }}
                    isDelete={true}
                    isEdit={true}
                    isAdd={true}
                />
            )
        }

        return (
            <RhyBusMetadataManagement
                key={getManagementDataKey('qa', qaArray)}
                config={qaConfig}
                data={qaArray}
                onAdd={async (item) => {
                    setLoading(true)
                    try {
                        const eKey = await saveQaItem(item)
                        if (eKey) await uploadCachedFiles(eKey, (item as any).AttachmentFiles ?? [], 'qanda')
                        await refreshQandA()
                        await loadSettingsQa()
                    } finally { setLoading(false) }
                }}
                onUpdate={async (item) => {
                    setLoading(true)
                    try {
                        const original = qaArray.find((a: any) => a.ID === (item as any).ID || a.eKey === (item as any).ID)
                        const eKey = await editQaItem({ ...original, ...item })
                        if (eKey) await uploadCachedFiles(eKey, (item as any).AttachmentFiles ?? [], 'qanda')
                        await refreshQandA()
                        await loadSettingsQa()
                    } finally { setLoading(false) }
                }}
                onDelete={async (item) => {
                    setLoading(true)
                    try {
                        await removeQaItem(item)
                        await refreshQandA()
                        await loadSettingsQa()
                    } finally { setLoading(false) }
                }}
                isDelete={true}
                isEdit={true}
                isAdd={true}
            />
        )
    }

    // Metadata management render
    const metadataManagementDataByKey: Record<string, any[]> = {
        'country': settingsMetadataCache['country'] ?? countryArray,
        'exa': settingsMetadataCache['exa'] ?? exaArray,
        'facility-category': settingsMetadataCache['facility-category'] ?? facilityCategoryArray,
        'implementing-agency': settingsMetadataCache['implementing-agency'] ?? implementingAgencyArray,
        'location': settingsMetadataCache['location'] ?? locationArray,
        'priority': settingsMetadataCache['priority'] ?? priorityArray,
        'program-type': settingsMetadataCache['program-type'] ?? programTypeArray,
        'rank': settingsMetadataCache['rank'] ?? rankArray,
        'training-type': settingsMetadataCache['training-type'] ?? trainingTypeArray,
    }

    const metadataManagementConfigByKey: Record<string, any> = {
        'country': countryConfig,
        'exa': exaConfig,
        'facility-category': facilityCategoryConfig,
        'implementing-agency': implementingAgencyConfig,
        'location': locationConfig,
        'priority': priorityConfig,
        'program-type': programTypeConfig,
        'rank': rankConfig,
        'training-type': trainingTypeConfig,
    }

    const metadataManagementRefreshByKey: Record<string, () => Promise<any>> = {
        'country': refreshCountry,
        'exa': refreshExa,
        'facility-category': refreshFacilityCategory,
        'implementing-agency': refreshImplementingAgency,
        'location': refreshLocation,
        'priority': refreshPriority,
        'program-type': refreshProgramType,
        'rank': refreshRank,
        'training-type': refreshTrainingType,
    }

    const renderMetadataManagementSection = (sectionKey: MetadataManagementTabKey) => {
        const metadataDefinition = metadataDefinitions.find((definition) => definition.key === sectionKey)
        if (metadataDefinition) {
            const refresh = metadataManagementRefreshByKey[sectionKey]
            return (
                <RhyBusMetadataManagement
                    key={getManagementDataKey(sectionKey, metadataManagementDataByKey[sectionKey] ?? [])}
                    config={metadataManagementConfigByKey[sectionKey]}
                    data={metadataManagementDataByKey[sectionKey] ?? []}
                    isAdd={true} isEdit={true} isDelete={true}
                    onAdd={async (item) => {
                        await saveMetadataItem(item, metadataDefinition.metaDataType)
                        await loadSettingsMetadataSection(sectionKey)
                        await refresh()
                    }}
                    onUpdate={async (item) => {
                        const dataArray = metadataManagementDataByKey[sectionKey] ?? []
                        const original = dataArray.find((a: any) => a.ID === (item as any).ID || a.eKey === (item as any).ID)
                        await editMetadataItem({ ...original, ...item }, metadataDefinition.metaDataType)
                        await loadSettingsMetadataSection(sectionKey)
                        await refresh()
                    }}
                    onDelete={async (item) => {
                        const dataArray = metadataManagementDataByKey[sectionKey] ?? []
                        const original = dataArray.find((a: any) => a.ID === (item as any).ID || a.eKey === (item as any).ID)
                        await removeMetadataItem({ ...original, ...item }, metadataDefinition.metaDataType)
                        await loadSettingsMetadataSection(sectionKey)
                        await refresh()
                    }}
                />
            )
        }
        if (sectionKey === 'tooltips') {
            return (
                <RhyBusMetadataManagement config={enrichedTooltipMetadataConfig} data={tooltipArray}
                    isEdit={true}
                    onUpdate={async (item) => {
                        const original = tooltipArray.find((t: any) => t.ToolTipID === (item as any).ToolTipID)
                        await editTooltipItem({ ...original, ...item, ID: original?.ID, eKey: original?.eKey, eTag: original?.eTag })
                        await refreshTooltips()
                    }}
                />
            )
        }
        if (sectionKey === 'update-build-version') {
            return (
                <div className="rb-metadata-settings" style={{ padding: '32px 24px' }}>
                    <RhyBusAlert
                        show={true}
                        size="md"
                        variant="confirm"
                        message={UPDATE_BUILD_VERSION}
                        confirmLabel="Yes"
                        cancelLabel="No"
                        okIcon="icon-rb-checked"
                        cancelIcon="icon-rb-close"
                        onConfirm={async () => {
                            await updateBuildVersionData()
                            setActiveMetadataManagementTab(metadataManagementTabs[0].key)
                            setMetadataTabsKey(k => k + 1)
                        }}
                        onCancel={() => {
                            setActiveMetadataManagementTab(metadataManagementTabs[0].key)
                            setMetadataTabsKey(k => k + 1)
                        }}
                    />
                </div>
            )
        }
        return null
    }

    const renderContentManagementTab = () => {
        const items = contentManagementTabs.map(section => ({
            tabtitle: section.title,
            is_active: activeContentManagementTab === section.key,
            content: (
                <div className="settings-nested-tab-panel" key={section.key}>
                    {renderContentManagementSection(section.key)}
                </div>
            )
        }))
        return (
            <div className="settings-nested-tabs-shell">
                <RhyBusTabs
                    anchorclass="font-14"
                    className="settings-nested-tabs settings-nested-tabs-content-management"
                    contentstyles="settings-nested-tabs-content"
                    items={items}
                    setActiveTab={(index: number) => setActiveContentManagementTab(contentManagementTabs[index].key)}
                    tablistclassName="settings-nested-tablist"
                    tabsize="large"
                    vertical="left"
                />
            </div>
        )
    }

    const renderMetadataManagementTab = () => {
        const items = metadataManagementTabs.map(section => ({
            tabtitle: section.title,
            is_active: activeMetadataManagementTab === section.key,
            content: (
                <div className="settings-nested-tab-panel" key={section.key}>
                    {renderMetadataManagementSection(section.key)}
                </div>
            )
        }))
        return (
            <div className="settings-nested-tabs-shell">
                <RhyBusTabs
                    key={metadataTabsKey}
                    anchorclass="font-14 "
                    className="settings-nested-tabs settings-nested-tabs-metadata-management"
                    contentstyles="settings-nested-tabs-content"
                    items={items}
                    setActiveTab={(index: number) => setActiveMetadataManagementTab(metadataManagementTabs[index].key)}
                    tablistclassName="settings-nested-tablist"
                    tabsize="large"
                    vertical="left"
                />
            </div>
        )
    }

    const renderReleaseNotesTab = () => (
        <div className="settings-release-notes">
            <RhyBusMetadataManagement
                config={releaseNotesConfig}
                data={releaseNotesArray}
                onAdd={async (item) => {
                    await saveReleaseNoteItem(item)
                    await refreshReleaseNotes()
                    await loadSettingsReleaseNotes()
                }}
                onUpdate={async (item) => {
                    const original = releaseNotesArray.find((r: any) => r.ID === (item as any).ID || r.eKey === (item as any).ID)
                    await editReleaseNoteItem({ ...original, ...item })
                    await refreshReleaseNotes()
                    await loadSettingsReleaseNotes()
                }}
                onDelete={async (item) => {
                    await removeReleaseNoteItem(item)
                    await refreshReleaseNotes()
                    await loadSettingsReleaseNotes()
                }}
                isDelete={true}
                isEdit={true}
                isAdd={true}
            />
        </div>
    )
    const renderUserManagementTab = () => (
        <div className="settings-user-management">
            {securityGroupError && (
                <div className="settings-error-message">{securityGroupError}</div>
            )}
            <RhyBusUserManagement
                roleUsers={userRoleFilter ? roleUsers.filter(r => r.role === userRoleFilter) : roleUsers}
                userAccessGroups={userAccessGroups}
                onAddUser={handleAddUser}
                onRemoveUser={handleRemoveUser}
                onDeleteUser={handleDeleteUser}
                onCheckUserPermissions={getUserRolesFiltered}
                onGetUserExistingRoles={getUserRolesFiltered}
                onPeoplePickerChange={handlePeoplePickerChange}
                peoplePickerData={peoplePickerSuggestions}
                // filters={[{
                //     id: 'settings-user-role-filter',
                //     label: 'User Role',
                //     options: sampleUserAccessGroups,
                //     value: userRoleFilter,
                //     onChange: setUserRoleFilter,
                //     onClear: () => setUserRoleFilter(''),
                //     placeholder: 'All'
                // }]}
                // onClearAllFilters={() => setUserRoleFilter('')}
                config={userManagementConfig}
            />

        </div>
    )

    const renderSiteFeedbackTab = () => {
        const feedbackItems = settingsSiteFeedbackData ?? siteFeedbackData ?? []
        const updatedActions = actions.map(action => ({
            ...action,
            onClick: () => action.onClick(feedbackItems)
        }))

        return (
            <div className="settings-site-feedback">
                <RhyBusFeedBackSettings
                    title="Site Feedback"
                    items={feedbackItems}
                    columns={feedbackcolumns}
                    actions={updatedActions}
                />
            </div>
        )
    }
    const tabs = settingsTabs.map((tab, index) => ({
        tabtitle: tab.title,
        is_active: activeTab === index,
        content: (
            <div className="settings-tab-panel" key={tab.id}>
                {tab.id === 'content-management' && renderContentManagementTab()}
                {tab.id === 'metadata-management' && renderMetadataManagementTab()}
                {tab.id === 'user-management' && renderUserManagementTab()}
                {tab.id === 'release-notes' && renderReleaseNotesTab()}
                {tab.id === 'site-feedback' && renderSiteFeedbackTab()}
            </div>
        )
    }))

    return (
        <main id="main-content">
            {(loading || dataLoading || securityGroupLoading) && <PageOverlay />}
            <RhyBusPageHeader pageTitle={'Settings'} pageHeaderIcon="icon-rb-settings" />
            <div className="container settings-layout">
                <div className="row">
                    <div className="col-12 mt-0">
                        <RhyBusCard
                            cardbodyclassName='py-0'
                            cardBody={
                                <RhyBusTabs
                                    anchorclass="font-15"
                                    className="settings-tabs-shell"
                                    contentstyles="settings-tabs-content"
                                    items={tabs}
                                    setActiveTab={setActiveTab}
                                    tablistclassName="overflow-auto"
                                    tabsize="xlarge"
                                />
                            }
                        />
                    </div>
                </div>
            </div>
            {showAlert &&
                <RhyBusAlert
                    show={showAlert}
                    size="md"
                    variant="confirm"
                    message={DELETE_USER}
                    confirmLabel="Yes"
                    cancelLabel="No"
                    okIcon="icon-rb-checked"
                    cancelIcon="icon-rb-close"
                    onConfirm={() => handleAlertClick('Yes')}
                    onCancel={() => setShowAlert(false)}
                />
            }
            {validationAlert &&
                <RhyBusAlert
                    show={validationAlert}
                    size="md"
                    variant="confirm"
                    message={VALIDATION_MSG}
                    confirmLabel="OK"
                    okIcon="icon-rb-checked"
                    onConfirm={() => setValidationAlert(false)}
                />
            }
        </main>
    )
}
